import { z } from "zod";

import {
  DEFAULT_DEPARTURE_SORT,
  DEPARTURE_SORTS,
  type DepartureSort,
} from "../dtos/traveler/departures.dto";

/**
 * The Departure Dates block (docs/features/departure-dates): its stored
 * settings, and the page-address filters a traveler sets on it.
 *
 * The settings are attributes on the block's stored root, written by its
 * traits; the storefront and the canvas read them through
 * `readDepartureDatesSettings`. The filters live in the page address, so a
 * view can be shared and Back steps through it (AC-07, AC-13). The server
 * render, the client that writes the address and the cache key all go
 * through `readDepartureFilters` and `departureFiltersQuery`, so they cannot
 * disagree about what an address means.
 */
export const DEPARTURE_DATES_HEADERS = [
  "stack",
  "toolbar",
  "panel",
  "strip",
  "compact",
] as const;
export type DepartureDatesHeader = (typeof DEPARTURE_DATES_HEADERS)[number];
export const DEPARTURE_DATES_LAYOUTS = ["banner", "rows", "grid"] as const;
export type DepartureDatesLayout = (typeof DEPARTURE_DATES_LAYOUTS)[number];
export const DEPARTURE_DATES_TOUR_MODES = ["all", "chosen"] as const;

/** A switch attribute: unset is the default; `false` is the one "off". */
const switchAttr = (fallback: boolean) =>
  z.preprocess(
    (v) =>
      v === undefined || v === null
        ? fallback
        : typeof v === "boolean"
          ? v
          : String(v).toLowerCase() !== "false",
    z.boolean(),
  );

/** A stored value this build does not know falls back, never throws. */
const choice = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values).catch(values[0]);

export const departureDatesSettingsSchema = z.object({
  tours: choice(DEPARTURE_DATES_TOUR_MODES),
  /** Hand-picked tours, by id, so a renamed tour stays picked. */
  tourIds: z.preprocess(
    (v) =>
      typeof v === "string" ? v.split(",").map((s) => s.trim()) : (v ?? []),
    z.array(z.string().uuid()).catch([]),
  ),
  /** The most departures the block lists; `null` is no ceiling. */
  max: z.coerce.number().int().min(1).nullable().catch(null).default(null),
  tourFilter: switchAttr(true),
  showFilter: switchAttr(true),
  hideSoldOut: switchAttr(false),
  /** A reduced date shows its original price struck through and the saving. */
  showSaving: switchAttr(true),
  details: switchAttr(true),
  openFirst: switchAttr(false),
  whatsapp: switchAttr(false),
  header: choice(DEPARTURE_DATES_HEADERS),
  layout: choice(DEPARTURE_DATES_LAYOUTS),
});
export type DepartureDatesSettings = z.infer<
  typeof departureDatesSettingsSchema
>;

export const readDepartureDatesSettings = (
  attributes: Record<string, unknown>,
): DepartureDatesSettings => departureDatesSettingsSchema.parse(attributes);

/**
 * The page-address parameters. `tour` repeats, one per ticked tour, which is
 * what the block's form-associated tour picker submits with no script.
 */
export const DEPARTURE_FILTER_PARAMS = {
  tour: "tour",
  year: "year",
  month: "month",
  sort: "sort",
} as const;

export interface DepartureFilters {
  /** Tour slugs, each once, in a fixed order. Empty is every tour. */
  tours: string[];
  year: number | null;
  month: number | null;
  sort: DepartureSort;
}

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const intIn = (raw: string | undefined, min: number, max: number) => {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n >= min && n <= max ? n : null;
};

/**
 * Reads an address leniently (AC-15): a value that is not well formed is
 * dropped, never an error. Whether a well-formed tour or year has any
 * departures is the API's answer, not this function's.
 */
export function readDepartureFilters(
  params: Pick<URLSearchParams, "get" | "getAll">,
): DepartureFilters {
  const sort = params.get(DEPARTURE_FILTER_PARAMS.sort);
  return {
    tours: [...new Set(params.getAll(DEPARTURE_FILTER_PARAMS.tour))]
      .filter((slug) => SLUG.test(slug))
      .sort(),
    year: intIn(
      params.get(DEPARTURE_FILTER_PARAMS.year) ?? undefined,
      2000,
      2100,
    ),
    month: intIn(params.get(DEPARTURE_FILTER_PARAMS.month) ?? undefined, 1, 12),
    sort: DEPARTURE_SORTS.find((s) => s === sort) ?? DEFAULT_DEPARTURE_SORT,
  };
}

/**
 * The one spelling of a filter state, for the address and the cache key: a
 * fixed parameter order, tours sorted, and defaults left out. Two travelers
 * who tick the same tours in a different order share one cached page.
 */
export function departureFiltersQuery(filters: DepartureFilters): string {
  const out = new URLSearchParams();
  for (const slug of [...new Set(filters.tours)].sort()) {
    out.append(DEPARTURE_FILTER_PARAMS.tour, slug);
  }
  if (filters.year !== null) {
    out.set(DEPARTURE_FILTER_PARAMS.year, String(filters.year));
  }
  if (filters.month !== null) {
    out.set(DEPARTURE_FILTER_PARAMS.month, String(filters.month));
  }
  if (filters.sort !== DEFAULT_DEPARTURE_SORT) {
    out.set(DEPARTURE_FILTER_PARAMS.sort, filters.sort);
  }
  return out.toString();
}
